import {
    ACCESS_TOKEN_MAX_AGE_SECOND,
    REFRESH_TOKEN_MAX_AGE_SECOND,
} from '@constants/auth';
import { SignInResponse } from '@models/auth';
import axios, { AxiosResponse } from 'axios';
import Cookies from 'universal-cookie';

const cookies = new Cookies(null, { path: '/' });

// instance를 쓰면 재발급 요청이 401 인터셉터에 다시 걸려 재귀가 되므로 bare axios를 쓴다.
export function reissueToken(refreshToken: string): Promise<AxiosResponse<SignInResponse>> {
    return axios.post(
        `${import.meta.env.VITE_API_URL}/v0/auth/token/refresh`,
        { refreshToken },
    );
}

// 서버가 재발급 때마다 refreshToken을 회전시키므로, 같은 토큰으로 두 번 요청하면
// 나중 요청이 400을 받는다. 여러 요청이 동시에 재발급을 시도할 수 있어
// 진행 중인 재발급이 있으면 그 결과를 공유한다(single-flight).
let inFlight: Promise<SignInResponse> | null = null;

async function requestAndStore(refreshToken: string): Promise<SignInResponse> {
    const res = (await reissueToken(refreshToken)).data;
    cookies.set('accessToken', res.token, {
        path: '/',
        maxAge: ACCESS_TOKEN_MAX_AGE_SECOND,
    });
    cookies.set('refreshToken', res.refreshToken, {
        path: '/',
        maxAge: REFRESH_TOKEN_MAX_AGE_SECOND,
    });
    return res;
}

// universal-cookie는 기본적으로 값을 JSON.parse 하므로, 숫자로만 이뤄진 토큰이
// number로 바뀌거나 정밀도를 잃을 수 있다. 원문으로 읽는다.
export function getAccessToken(): string | undefined {
    return cookies.get('accessToken', { doNotParse: true });
}

export function getRefreshToken(): string | undefined {
    return cookies.get('refreshToken', { doNotParse: true });
}

// 재발급 후 두 토큰을 쿠키에 반영하고 새 토큰들을 돌려준다.
// 훅 밖(인터셉터)에서도 호출할 수 있도록 react-cookie가 아닌 universal-cookie를 직접 쓴다.
// refreshToken을 생략하면 쿠키에서 최신 값을 읽는다(회전 직후 옛 토큰 재사용 방지).
export function reissueAndStoreToken(refreshToken?: string): Promise<SignInResponse> {
    if (inFlight) {
        return inFlight;
    }
    const token = refreshToken ?? getRefreshToken();
    if (!token) {
        return Promise.reject(new Error('Refresh token not found'));
    }
    inFlight = requestAndStore(token)
        .catch((e) => {
            console.error('토큰 재발급 실패', e);
            throw e;
        })
        .finally(() => {
            inFlight = null;
        });
    return inFlight;
}
